import { Dispatch, SetStateAction, useEffect, useState, useImperativeHandle, Ref, ReactNode } from 'react';
import { SupportRequestDtoIn } from '@/models/SupportRequestDtoIn';
import SupportedNameFilter from './filters/SupportedNameFilter';
import DateFilter from './filters/DateFilter';
import StatusFilter from '@/components/filters/StatusFilter/StatusFilter';
import StatusChevronFilter from '@/components/filters/StatusFilter/StatusChevronFilter';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import styles from '@/components/filters/FiltersArea.module.scss';
import {
  statusGroups,
  StatusGroup,
  getStatusGroup,
  getAggregatedStatus,
  mobileStatusChevrons,
  MobileStatusChevron,
} from '@/helpers/requestStatus';

interface FiltersAreaProps {
  requests: SupportRequestDtoIn[];
  setFilteredRequests: Dispatch<SetStateAction<SupportRequestDtoIn[]>>;
  selectedSupportedName: string | null;
  setSelectedSupportedName: (supportedName: string | null) => void;
  dateFrom: Date;
  dateTo: Date;
  onDateChange: (dateFrom: Date, dateTo: Date) => void;
  actionButton?: ReactNode;
  /**
   * מזהי קבוצות סטטוס שהמסך נפתח כשהן מסומנות.
   *
   * "תמונת מצב" שולחת לכאן מ"דורש טיפול", ובלי זה הקישור נחת על כל הבקשות
   * ולא על אלה שתקועות - כלומר הרכז היה צריך לחפש בעצמו את מי שהמסך הקודם
   * כבר זיהה עבורו. הערך משמש כמצב התחלתי בלבד, ומהרגע שהרכז נוגע בסינון
   * הוא שולט בו.
   */
  initialStatusGroupIds?: string[];
  ref?: Ref<{ resetFilters: () => void }>;
  /** HAT-1212: העמוד באמצע refetch. עובר ל-DateFilter → OptionsRangeFilter. */
  isBusy?: boolean;
}

export interface FilterState {
  selectedStatuses: string[];
}

const matchesSupportedName = (request: SupportRequestDtoIn, supportedName: string | null): boolean => {
  return !supportedName || (request.supportedContact != null && request.supportedContact.firstName + ' ' + request.supportedContact.lastName === supportedName);
};

const matchesStatus = (request: SupportRequestDtoIn, selectedStatuses: string[]): boolean => {
  if (selectedStatuses.length === 0) {
    return true;
  }
  const aggregatedStatus = getAggregatedStatus(request.statuses);
  return selectedStatuses.includes(aggregatedStatus);
};

const FiltersArea = ({
  requests,
  setFilteredRequests,
  selectedSupportedName,
  setSelectedSupportedName,
  dateFrom,
  dateTo,
  onDateChange,
  actionButton,
  initialStatusGroupIds,
  ref,
  isBusy,
}: FiltersAreaProps) => {
  const { isMobile } = useDeviceDetection();
  const [filterState, setFilterState] = useState<FilterState>(() => ({
    selectedStatuses: statusGroups.filter((group) => initialStatusGroupIds?.includes(group.id)).flatMap((group) => group.statuses),
  }));

  const groupCounts: { [groupId: string]: number } = {};

  statusGroups.forEach((group) => {
    groupCounts[group.id] = 0;
  });

  requests.forEach((request) => {
    const aggregatedStatus = getAggregatedStatus(request.statuses);
    const group = getStatusGroup(aggregatedStatus);

    if (group) {
      groupCounts[group.id]++;
    }
  });

  const resetAllFilters = () => {
    setFilterState({
      selectedStatuses: [],
    });

    setSelectedSupportedName(null);
  };

  const handleStatusGroupChange = (statusGroup: StatusGroup) => {
    const isGroupAlreadySelected = statusGroup.statuses.every((status) => filterState.selectedStatuses.includes(status));

    setFilterState({
      ...filterState,
      selectedStatuses: isGroupAlreadySelected
        ? filterState.selectedStatuses.filter((status) => !statusGroup.statuses.includes(status))
        : [...filterState.selectedStatuses, ...statusGroup.statuses],
    });
  };

  /**
   * SPR-1205 — same toggle semantics as desktop pills, but against the
   * curated mobile chevron status lists (Archive unions several groups;
   * exceptions-committee is a subset of PendingApproval).
   */
  const handleMobileChevronToggle = (item: MobileStatusChevron) => {
    const isAlreadySelected = item.statuses.length > 0 && item.statuses.every((status) => filterState.selectedStatuses.includes(status));

    setFilterState({
      ...filterState,
      selectedStatuses: isAlreadySelected
        ? filterState.selectedStatuses.filter((status) => !item.statuses.includes(status))
        : [...new Set([...filterState.selectedStatuses, ...item.statuses])],
    });
  };

  useEffect(() => {
    const filtered = requests.filter((request) => {
      return matchesSupportedName(request, selectedSupportedName) && matchesStatus(request, filterState.selectedStatuses);
    });

    setFilteredRequests(filtered);
  }, [filterState, selectedSupportedName, requests, setFilteredRequests]);

  useImperativeHandle(ref, () => ({
    resetFilters: resetAllFilters,
  }));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between items-center max-md:flex-col max-md:items-stretch max-md:gap-3">
        <DateFilter dateFrom={dateFrom} dateTo={dateTo} onChange={onDateChange} isBusy={isBusy} />
        {actionButton}
      </div>
      {/*
       * SPR-1205 — במסך צר פס chevron במלוא הרוחב (Figma 186:68), מחוץ
       * לשורת הקפסולות, כדי שגלילת הסטטוס לא תתערבב עם פילטר השם.
       */}
      {isMobile && (
        <StatusChevronFilter
          items={mobileStatusChevrons}
          isSelected={(item) => item.statuses.length > 0 && item.statuses.every((status) => filterState.selectedStatuses.includes(status))}
          onToggle={handleMobileChevronToggle}
        />
      )}
      <div className={styles.filtersContainer} data-board-filters>
        {!isMobile &&
          statusGroups.map((statusGroup) => (
            <StatusFilter
              key={statusGroup.id}
              title={statusGroup.id}
              isSelected={statusGroup.statuses.length > 0 && statusGroup.statuses.every((status) => filterState.selectedStatuses.includes(status))}
              onClick={() => handleStatusGroupChange(statusGroup)}
              iconColor={statusGroup.iconColor}
              count={groupCounts[statusGroup.id]}
            />
          ))}

        <SupportedNameFilter selectedSupportedName={selectedSupportedName} setSelectedSupportedName={setSelectedSupportedName} />
      </div>
    </div>
  );
};

export default FiltersArea;
